import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { mergeRegister } from '@lexical/utils'
import * as Sentry from '@sentry/react'
import classNames from 'classnames'
import { $getSelection, $isRangeSelection, type EditorState, type EditorThemeClasses, type SerializedEditorState } from 'lexical'
import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLatest } from 'react-use'
import { defaultLexicalTheme } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/defaultLexicalTheme'
import { EditablePlugin } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/EditablePlugin/EditablePlugin'
import styles from './EmailEditor.module.scss'
import { type ActiveSlot, EmailToolbar } from './EmailToolbar'
import { type EmailVariable } from './emailTemplateTypes'
import { EMAIL_NODES } from './nodes'
import { $findSlot, $getSlots } from './nodes/SlotNode'
import { registerContentRules } from './rules/contentRules'
import { registerEditingBehaviour } from './rules/editingBehaviour'
import { VariableLabelsContext } from './VariableLabelsContext'
import { SAFE_URL } from './validate/validateState'
import { VariableTypeaheadPlugin } from './VariableTypeaheadPlugin'

const EMAIL_THEME: EditorThemeClasses = { text: defaultLexicalTheme.text, list: defaultLexicalTheme.list, link: defaultLexicalTheme.link }

const isSafeUrl = (url: string): boolean => SAFE_URL.test(url) && !/[{}]/.test(url)

const RulesPlugin: FC<{ onRejected: () => void }> = ({ onRejected }) => {
  const [editor] = useLexicalComposerContext()
  const onRejectedRef = useLatest(onRejected)
  useEffect(
    () =>
      mergeRegister(
        registerContentRules(editor),
        registerEditingBehaviour(editor, () => onRejectedRef.current()),
      ),
    [editor, onRejectedRef],
  )
  return null
}

const ActiveSlotPlugin: FC<{ onActive: (slot: ActiveSlot | null) => void }> = ({ onActive }) => {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    const active: { key: string | null } = { key: null }
    return editor.registerUpdateListener(({ editorState }) => {
      const next = editorState.read(() => {
        const selection = $getSelection()
        const slot = $isRangeSelection(selection) ? $findSlot(selection.anchor.getNode()) : null
        return slot ? { key: slot.getKey(), format: slot.getAttrs().slotFormat } : null
      })
      if ((next?.key ?? null) === active.key) {
        return
      }
      if (active.key) {
        editor.getElementByKey(active.key)?.classList.remove(styles.slotActive)
      }
      if (next) {
        editor.getElementByKey(next.key)?.classList.add(styles.slotActive)
      }
      active.key = next?.key ?? null
      onActive(next ? { format: next.format } : null)
    })
  }, [editor, onActive])
  return null
}

const IssueMarkersPlugin: FC<{ issueNumbers: ReadonlyMap<string, number> }> = ({ issueNumbers }) => {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    const apply = () =>
      editor.getEditorState().read(() =>
        $getSlots().forEach((slot) => {
          const element = editor.getElementByKey(slot.getKey())
          const number = issueNumbers.get(slot.getAttrs().slotId)
          element?.classList.toggle(styles.slotInvalid, number !== undefined)
          if (number === undefined) {
            element?.style.removeProperty('--issue-number')
          } else {
            element?.style.setProperty('--issue-number', `"${number}"`)
          }
        }),
      )
    apply()
    return editor.registerUpdateListener(apply)
  }, [editor, issueNumbers])
  return null
}

export interface EmailEditorProps {
  initialState: SerializedEditorState
  variables: EmailVariable[]
  issueNumbers: ReadonlyMap<string, number>
  readOnly: boolean
  toolbarDisabled?: boolean
  toolbarContainer: HTMLElement | null
  floatingAnchor?: HTMLElement | null
  onChange: (state: SerializedEditorState) => void
  onRejected: () => void
}

export const EmailEditor: FC<PropsWithChildren<EmailEditorProps>> = ({
  initialState,
  variables,
  issueNumbers,
  readOnly,
  toolbarDisabled,
  toolbarContainer,
  floatingAnchor = null,
  onChange,
  onRejected,
  children,
}) => {
  const [initialConfig] = useState(() => ({
    namespace: 'lexical-email',
    theme: EMAIL_THEME,
    nodes: EMAIL_NODES,
    editorState: JSON.stringify(initialState),
    editable: !readOnly,
    onError: (error: Error) => {
      Sentry.captureException(error)
    },
  }))
  const [activeSlot, setActiveSlot] = useState<ActiveSlot | null>(null)
  const labels = useMemo(() => new Map(variables.map((variable) => [variable.name, variable.label])), [variables])

  return (
    <VariableLabelsContext.Provider value={labels}>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={<ContentEditable className={classNames(styles.editor, 'w-100')} aria-label='Email' />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin validateUrl={isSafeUrl} />
        <RulesPlugin onRejected={onRejected} />
        <ActiveSlotPlugin onActive={setActiveSlot} />
        <IssueMarkersPlugin issueNumbers={issueNumbers} />
        <EditablePlugin isEditable={!readOnly} />
        {readOnly ? null : <VariableTypeaheadPlugin variables={variables} />}
        <OnChangePlugin ignoreSelectionChange onChange={(state: EditorState) => onChange(state.toJSON())} />
        {children}
        {toolbarContainer
          ? createPortal(
              <EmailToolbar variables={variables} activeSlot={activeSlot} disabled={readOnly || Boolean(toolbarDisabled)} floatingAnchor={floatingAnchor} />,
              toolbarContainer,
            )
          : null}
      </LexicalComposer>
    </VariableLabelsContext.Provider>
  )
}
